import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { getConsent, setConsent, isTrackedRoute } from '@/lib/analytics';

/**
 * GDPR cookie consent modal. Centered, dimmed backdrop, BLOCKS interaction
 * with the page until the user explicitly picks Accept or Reject. No X
 * close, no click-outside dismiss, no ESC — GDPR allows requiring a choice
 * as long as both options are equally prominent (they share weight, colour
 * intensity, and size below).
 *
 * Only shown on tracked marketing/blog routes; dashboard and guest portal
 * never see it because GA isn't loaded there.
 */
export function CookieConsentBanner() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const onTrackedRoute = isTrackedRoute(location.pathname);

  useEffect(() => {
    if (!onTrackedRoute) {
      setVisible(false);
      return;
    }
    if (getConsent() === null) {
      const id = window.setTimeout(() => setVisible(true), 300);
      return () => window.clearTimeout(id);
    }
  }, [onTrackedRoute]);

  // Lock body scroll while the modal is up so the page underneath can't be
  // interacted with.
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  if (!visible || !onTrackedRoute) return null;

  const accept = () => {
    setConsent('granted');
    setVisible(false);
  };
  const reject = () => {
    setConsent('denied');
    setVisible(false);
  };

  const cookiesHref = `/${i18n.language === 'en' ? 'en' : 'hr'}/cookies`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-modal-title"
      aria-describedby="cookie-modal-desc"
      className="fixed inset-0 z-9999 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <div className="w-full max-w-md rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-2xl p-6">
        <div className="flex flex-col items-center text-center">
          <div className="rounded-xl bg-cyan-500/10 p-3 text-cyan-600">
            <Cookie className="h-7 w-7" />
          </div>
          <h2
            id="cookie-modal-title"
            className="mt-4 text-lg font-semibold text-[hsl(var(--foreground))]"
          >
            {t('cookieBanner.title', 'Koristimo kolačiće')}
          </h2>
          <p
            id="cookie-modal-desc"
            className="mt-2 text-sm text-[hsl(var(--muted-foreground))]"
          >
            {t(
              'cookieBanner.description',
              'Koristimo nužne kolačiće za rad aplikacije. Uz vaš pristanak koristimo i analitičke kolačiće (Google Analytics) za poboljšanje iskustva.'
            )}{' '}
            <Link
              to={cookiesHref}
              className="underline hover:text-[hsl(var(--foreground))]"
            >
              {t('cookieBanner.learnMore', 'Saznaj više')}
            </Link>
          </p>

          {/* Both buttons share size, padding, and font weight. Colours differ
              (filled vs outlined) but contrast is balanced so neither is
              visually dominant — required for valid GDPR consent. */}
          <div className="mt-6 grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={reject}
              className="rounded-lg border border-[hsl(var(--border))] px-4 py-2.5 text-sm font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors"
            >
              {t('cookieBanner.reject', 'Samo nužni')}
            </button>
            <button
              type="button"
              onClick={accept}
              className="rounded-lg bg-cyan-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-cyan-700 transition-colors"
            >
              {t('cookieBanner.accept', 'Prihvati sve')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
